/** Converts editable shapes (local coordinates + transform) into flattened design-space geometry. */
import { t } from '../../i18n/i18n';
import { type PathNode, flattenPath, mapNodes } from '../geometry/bezier';
import { cleanPolygons } from '../geometry/clip';
import {
  type BBox,
  type PolygonWithHoles,
  type Polyline,
  type Ring,
  bboxOf,
  emptyBBox,
  ringArea,
  ringSelfIntersections,
} from '../geometry/polygon';
import { type Vec, degToRad } from '../geometry/vec';
import type { Issue } from './issues';
import type { EmbObject, Shape, Transform } from './types';

export const IDENTITY: Transform = { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 };

/** Flattening tolerance for curves, mm. Well below thread width (~0.4 mm). */
export const FLATTEN_TOL = 0.03;

export function applyTransform(p: Vec, t: Transform): Vec {
  const sx = p.x * t.scaleX;
  const sy = p.y * t.scaleY;
  const r = degToRad(t.rotation);
  const c = Math.cos(r);
  const s = Math.sin(r);
  return { x: sx * c - sy * s + t.x, y: sx * s + sy * c + t.y };
}

export function invertTransform(p: Vec, t: Transform): Vec {
  const dx = p.x - t.x;
  const dy = p.y - t.y;
  const r = degToRad(-t.rotation);
  const c = Math.cos(r);
  const s = Math.sin(r);
  const rx = dx * c - dy * s;
  const ry = dx * s + dy * c;
  return { x: t.scaleX !== 0 ? rx / t.scaleX : 0, y: t.scaleY !== 0 ? ry / t.scaleY : 0 };
}

export const isIdentity = (t: Transform): boolean =>
  t.x === 0 && t.y === 0 && t.rotation === 0 && t.scaleX === 1 && t.scaleY === 1;

/** Applies the transform to all nodes of a shape, returning a shape in design coordinates. */
export function transformShape(shape: Shape, t: Transform): Shape {
  if (isIdentity(t)) return shape;
  const f = (p: Vec) => applyTransform(p, t);
  switch (shape.kind) {
    case 'area':
      return { kind: 'area', outer: mapNodes(shape.outer, f), holes: shape.holes.map((h) => mapNodes(h, f)) };
    case 'line':
      return { kind: 'line', nodes: mapNodes(shape.nodes, f), closed: shape.closed };
    case 'column':
      return { kind: 'column', railA: mapNodes(shape.railA, f), railB: mapNodes(shape.railB, f) };
  }
}

export type FlatGeometry =
  | { kind: 'area'; polygons: PolygonWithHoles[]; rings: Ring[] }
  | { kind: 'line'; points: Polyline; closed: boolean }
  | { kind: 'column'; railA: Polyline; railB: Polyline };

export interface ResolvedGeometry {
  geometry: FlatGeometry;
  issues: Issue[];
}

const flat = (nodes: PathNode[], closed: boolean): Vec[] => flattenPath(nodes, closed, FLATTEN_TOL);

/** Resolves an object to flattened design-space geometry and reports geometric problems. */
export function resolveGeometry(obj: Pick<EmbObject, 'id' | 'shape' | 'transform'>): ResolvedGeometry {
  const shape = transformShape(obj.shape, obj.transform);
  const issues: Issue[] = [];
  switch (shape.kind) {
    case 'area': {
      const rings = [flat(shape.outer, true), ...shape.holes.map((h) => flat(h, true))].filter((r) => r.length >= 3);
      for (const r of rings) {
        const hits = ringSelfIntersections(r, 1);
        if (hits.length) {
          issues.push({
            severity: 'warning',
            code: 'self-intersection',
            objectId: obj.id,
            point: hits[0],
            message: t('The outline crosses itself. It is interpreted with the even-odd rule; fix the nodes for a predictable result.'),
          });
          break;
        }
      }
      const polygons = cleanPolygons(rings.map((r) => ({ outer: r, holes: [] })));
      if (polygons.length === 0 || polygons.every((p) => ringArea(p.outer) < 0.01)) {
        issues.push({ severity: 'error', code: 'degenerate', objectId: obj.id, message: t('The shape has no area. Delete the object or draw the shape again.') });
      }
      return { geometry: { kind: 'area', polygons, rings }, issues };
    }
    case 'line': {
      const points = flat(shape.nodes, shape.closed);
      if (points.length < 2) {
        issues.push({ severity: 'error', code: 'degenerate', objectId: obj.id, message: t('The line needs at least two points.') });
      }
      return { geometry: { kind: 'line', points, closed: shape.closed && points.length >= 3 }, issues };
    }
    case 'column': {
      const railA = flat(shape.railA, false);
      const railB = flat(shape.railB, false);
      if (railA.length < 2 || railB.length < 2) {
        issues.push({ severity: 'error', code: 'degenerate', objectId: obj.id, message: t('The satin column needs two rails with at least two points each.') });
      }
      return { geometry: { kind: 'column', railA, railB }, issues };
    }
  }
}

export function geometryPoints(g: FlatGeometry): Vec[] {
  switch (g.kind) {
    case 'area':
      return g.polygons.flatMap((p) => [p.outer, ...p.holes].flat());
    case 'line':
      return g.points;
    case 'column':
      return [...g.railA, ...g.railB];
  }
}

/** Bounding box of an object's shape in design coordinates (control handles excluded). */
export function objectBBox(obj: Pick<EmbObject, 'shape' | 'transform'>): BBox {
  const shape = transformShape(obj.shape, obj.transform);
  const b = emptyBBox();
  switch (shape.kind) {
    case 'area':
      bboxOf(flat(shape.outer, true), b);
      break;
    case 'line':
      bboxOf(flat(shape.nodes, shape.closed), b);
      break;
    case 'column':
      bboxOf(flat(shape.railA, false), b);
      bboxOf(flat(shape.railB, false), b);
      break;
  }
  return b;
}

/** Bounding box of the shape in local coordinates (used by the editor for handles). */
export function localBBox(shape: Shape): BBox {
  return objectBBox({ shape, transform: IDENTITY });
}
